import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';

/**
 * Inline pill components used throughout the Reltio-style chat.
 *
 * 5 kinds, each with a distinct visual treatment:
 *   - RegexPill        — monospace, dark slate, slightly rounded; for `^\d{10}$`
 *   - CommandPill      — soft accent bg, smaller; for `confirm`, `proceed`
 *   - ValuePill        — monospace, neutral dark; for sample data values
 *                        (optional count badge: "[Male] (1,883)")
 *   - FilePathPill     — monospace, slightly green-tinted; for paths/tables
 *   - UUIDPill         — monospace, copy-on-click feedback
 *
 * All pills support copy-on-click; visible Check icon flashes on success.
 * Hover lifts the pill slightly + brightens border for tactile feel.
 */

interface BasePillProps {
  value: string;
  copyable?: boolean;
  className?: string;
}

function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = (text: string) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }).catch(() => {/* ignore */});
  };
  return { copied, copy };
}

/* ───────── RegexPill ───────── */

export const RegexPill: React.FC<BasePillProps> = ({ value, copyable = true, className = '' }) => {
  const { copied, copy } = useCopy();
  return (
    <span
      onClick={copyable ? () => copy(value) : undefined}
      className={`
        inline-flex items-center gap-1.5
        font-mono text-[12px] leading-5
        px-2 py-0.5 rounded-md
        bg-cargo-panel border border-cargo-border
        text-cargo-text
        transition-all duration-150
        ${copyable ? 'cursor-pointer hover:bg-cargo-hover hover:border-cargo-border hover:-translate-y-px' : ''}
        ${className}
      `}
      title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      <span>{value}</span>
      {copyable && (copied ? <Check className="w-3 h-3 text-cargo-success" /> : null)}
    </span>
  );
};

/* ───────── CommandPill ───────── */

export const CommandPill: React.FC<BasePillProps> = ({ value, copyable = true, className = '' }) => {
  const { copied, copy } = useCopy();
  return (
    <span
      onClick={copyable ? () => copy(value) : undefined}
      className={`
        inline-flex items-center gap-1
        font-mono text-[12px] leading-5
        px-2 py-0.5 rounded
        bg-cargo-accent-light border border-cargo-accent
        text-cargo-accent
        transition-all duration-150
        ${copyable ? 'cursor-pointer hover:bg-cargo-accent-light hover:border-cargo-accent hover:-translate-y-px' : ''}
        ${className}
      `}
      title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      {value}
      {copyable && (copied ? <Check className="w-3 h-3 text-cargo-success" /> : null)}
    </span>
  );
};

/* ───────── ValuePill (with optional count) ───────── */

interface ValuePillProps extends BasePillProps {
  // null = truncated GX SUMMARY sample (real frequency unknown) →
  // render the value only. The body already guards `count != null`.
  count?: number | null;
}

export const ValuePill: React.FC<ValuePillProps> = ({
  value, count, copyable = true, className = '',
}) => {
  const { copied, copy } = useCopy();
  return (
    <span className={`inline-flex items-baseline gap-1 ${className}`}>
      <span
        onClick={copyable ? () => copy(value) : undefined}
        className={`
          inline-flex items-center gap-1
          font-mono text-[12px] leading-5
          px-2 py-0.5 rounded
          bg-cargo-hover border border-cargo-border
          text-cargo-text
          transition-all duration-150
          ${copyable ? 'cursor-pointer hover:bg-cargo-hover hover:border-cargo-border hover:-translate-y-px' : ''}
        `}
        title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
      >
        {value}
        {copyable && (copied ? <Check className="w-3 h-3 text-cargo-success" /> : null)}
      </span>
      {count != null && (
        <span className="text-cargo-muted text-[12px]">({count.toLocaleString()})</span>
      )}
    </span>
  );
};

/* ───────── FilePathPill ───────── */

export const FilePathPill: React.FC<BasePillProps> = ({ value, copyable = true, className = '' }) => {
  const { copied, copy } = useCopy();
  return (
    <span
      onClick={copyable ? () => copy(value) : undefined}
      className={`
        inline-flex items-center gap-1
        font-mono text-[12px] leading-5
        px-2 py-0.5 rounded-md
        bg-cargo-panel border border-cargo-border
        text-cargo-text
        transition-all duration-150
        ${copyable ? 'cursor-pointer hover:bg-cargo-hover hover:border-cargo-border hover:-translate-y-px' : ''}
        ${className}
      `}
      title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      {value}
      {copyable && (copied ? <Check className="w-3 h-3 text-cargo-success" /> : null)}
    </span>
  );
};

/* ───────── UUIDPill ───────── */

export const UUIDPill: React.FC<BasePillProps> = ({ value, copyable = true, className = '' }) => {
  const { copied, copy } = useCopy();
  return (
    <span
      onClick={copyable ? () => copy(value) : undefined}
      className={`
        inline-flex items-center gap-1.5
        font-mono text-[12px] leading-5
        px-2 py-0.5 rounded
        bg-indigo-900/40 border border-indigo-500/30
        text-indigo-100
        transition-all duration-150
        ${copyable ? 'cursor-pointer hover:bg-indigo-900/60 hover:border-indigo-400/50 hover:-translate-y-px' : ''}
        ${className}
      `}
      title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      <span>{value}</span>
      {copyable && (
        copied
          ? <Check className="w-3 h-3 text-cargo-success" />
          : <Copy className="w-3 h-3 text-indigo-300/60" />
      )}
    </span>
  );
};

/* ───────── QuotedExamplePill (for "Change Gender pattern...") ───────── */

export const QuotedExamplePill: React.FC<BasePillProps> = ({ value, copyable = true, className = '' }) => {
  const { copied, copy } = useCopy();
  return (
    <span
      onClick={copyable ? () => copy(value) : undefined}
      className={`
        inline-flex items-center gap-1
        font-mono text-[12px] leading-5
        px-2 py-0.5 rounded
        bg-cargo-panel border border-cargo-border
        text-cargo-text
        transition-all duration-150
        ${copyable ? 'cursor-pointer hover:bg-cargo-hover hover:border-cargo-border hover:-translate-y-px' : ''}
        ${className}
      `}
      title={copyable ? (copied ? 'Copied!' : 'Click to copy') : undefined}
    >
      "{value}"
      {copyable && (copied ? <Check className="w-3 h-3 text-cargo-success" /> : null)}
    </span>
  );
};
